import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'MAINTENANCE_COUNTRY_EDIT_FORM';
const PERMISSION_SP = 'COUNTRY';

function CountryEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/countries/${pageId}`;

  const preProcessRequestData = data => {
    const { isActive, id, ...other } = data;
    return {
      isActive: isActive === 'true',
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { ...other } = data;
    return {
      ...other,
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/countries/${pageId}`;
    try {
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        return {
          data: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        data: null,
        errors: response?.errors || null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const onSubmit = async data => {
    try {
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'COUNTRY_WAS_SAVED_SUCCESSFULLY',
            defaultMessage: 'Country was saved successfully',
          }),
        );
        return {
          nextValues: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'COUNTRIES',
        defaultMessage: 'Countries',
      }),
      icon: IconLibrary.Countries,
      onClick: () => {
        navigate('/countries');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_COUNTRY',
        defaultMessage: 'Edit Country',
      }),
      icon: IconLibrary.Country,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_COUNTRY',
        defaultMessage: 'Create Country',
      }),
      Icon: IconLibrary.Country,
      variant: 'outlined',
      onClick: () => navigate('/countries/create'),
      permissionName: 'CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES',
    },
    {
      label: formatMessage({
        id: 'LIST_COUNTRIES',
        defaultMessage: 'List Countries',
      }),
      Icon: IconLibrary.Countries,
      variant: 'outlined',
      onClick: () => navigate('/countries'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'EDIT_COUNTRY',
          defaultMessage: 'Edit Country',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={editSchema}
            validationSchema={validationSchema(formatMessage, getApiUrl, get)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_COUNTRY',
                  defaultMessage: 'Save Country',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(CountryEdit);
